import React from "react";
import {
  Box,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const BORDER = "#E5E7EB";
const TEXT = "#111827";
const MUTED = "#667085";
export const JOB_REJECTION_REASONS = ["Experience", "Skills", "Compensation", "Location", "Work Authorization", "Culture / Team Fit", "Another Candidate Preferred", "Candidate Accepted Another Offer", "Candidate Rejected", "Role Changed", "Position Closed", "Other"];
export const JOB_DISPOSITIONS = ["Match to Other Jobs", "Keep Active", "Talent Pool", "Revisit in 30 Days", "Revisit in 90 Days", "Executive Talent Pool", "Archive", "Do Not Contact"];
const DECISION_SOURCES = ["Candidate", "Recruiter", "Hiring Manager", "Client / Hiring Team", "System / Administrative"];

const REJECTED_BY_LABELS = {
  candidate: "Candidate",
  recruiter: "Recruiter",
  hiring_manager: "Hiring Manager",
  system_administrative: "System / Administrative",
};

const REJECTION_REASONS = {
  candidate: [
    "Accepted Another Offer",
    "Compensation",
    "Remote / Location",
    "Timeline",
    "Role Not Right",
    "Company Not Right",
    "Personal Reasons",
    "Stopped Looking",
    "Lost Contact",
    "Other",
  ],
  recruiter: [
    "Experience",
    "Technical Skills",
    "Communication",
    "Compensation",
    "Location",
    "Work Authorization",
    "Availability",
    "Duplicate",
    "Not Qualified",
    "Other",
  ],
  hiring_manager: [
    "Experience",
    "Technical Skills",
    "Interview Performance",
    "Culture / Team Fit",
    "Leadership",
    "Compensation",
    "Overqualified",
    "Better Candidate Selected",
    "Position Changed",
    "Other",
  ],
  system_administrative: [
    "Duplicate",
    "Already in Process",
    "Position Closed",
    "Hiring Freeze",
    "Background Check",
    "Compliance",
    "Other",
  ],
};

const normalizeRoleToken = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const getRejectedByValueFromRole = (role) => {
  const normalizedRole = normalizeRoleToken(role);

  if (!normalizedRole) return "recruiter";
  if (normalizedRole === "candidate") return "candidate";
  if (
    normalizedRole === "hiring_manager" ||
    (normalizedRole.includes("hiring") && normalizedRole.includes("manager")) ||
    normalizedRole === "client"
  ) {
    return "hiring_manager";
  }
  if (
    normalizedRole === "admin" ||
    normalizedRole === "administrator" ||
    normalizedRole.startsWith("system") ||
    normalizedRole.includes("administr")
  ) {
    return "system_administrative";
  }

  return "recruiter";
};

const getRejectedByLabel = (value) =>
  REJECTED_BY_LABELS[value] || REJECTED_BY_LABELS.recruiter;

export default function RejectedStageDialog({
  open = false,
  loading = false,
  candidateCount = 1,
  currentUserRole = "",
  jobSpecific = false,
  onClose,
  onSubmit,
}) {
  const [reason, setReason] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [errors, setErrors] = React.useState({});
  const [disposition, setDisposition] = React.useState("Match to Other Jobs");
  const [decisionBy, setDecisionBy] = React.useState("Client / Hiring Team");
  const [submitting, setSubmitting] = React.useState(false);
  const submittingRef = React.useRef(false);
  const busy = loading || submitting;
  const rejectedBy = React.useMemo(
    () => getRejectedByValueFromRole(currentUserRole),
    [currentUserRole]
  );
  const rejectedByLabel = React.useMemo(
    () => getRejectedByLabel(rejectedBy),
    [rejectedBy]
  );

  React.useEffect(() => {
    if (!open) return;
    setReason("");
    setNotes("");
    setErrors({});
    setDisposition("Match to Other Jobs");
    setDecisionBy("Client / Hiring Team");
  }, [open]);

  const reasonOptions = React.useMemo(
    () => {
      if (jobSpecific) return JOB_REJECTION_REASONS;
      const reasons = REJECTION_REASONS[rejectedBy] || [];
      if (rejectedBy !== "system_administrative") return reasons;
      return [
        ...new Set([...reasons, ...REJECTION_REASONS.recruiter].filter((option) => option !== "Other")),
        "Other",
      ];
    },
    [jobSpecific, rejectedBy]
  );

  React.useEffect(() => {
    setReason((prevReason) => (reasonOptions.includes(prevReason) ? prevReason : ""));
  }, [reasonOptions]);

  const handleReasonChange = React.useCallback((event) => {
    setReason(String(event.target.value || ""));
    setErrors((prev) => ({ ...prev, reason: "" }));
  }, []);

  const handleNotesChange = React.useCallback((event) => {
    setNotes(event.target.value || "");
  }, []);

  const handleSubmit = React.useCallback(async () => {
    if (loading || submittingRef.current) return;
    const nextErrors = {};

    if (!reason) {
      nextErrors.reason = "Reason is required.";
    }

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setErrors({});
    try {
    await onSubmit?.(jobSpecific ? {
      reason: reason.trim(), note: notes.trim(), disposition, decisionBy,
    } : {
      rejectedBy,
      rejectedByLabel,
      reason: String(reason || "").trim(),
      note: String(notes || "").trim(),
    });
    } catch (error) {
      setErrors({ submit: error?.message || "Unable to save rejection. Please try again." });
    } finally { submittingRef.current = false; setSubmitting(false); }
  }, [loading, jobSpecific, disposition, decisionBy, notes, onSubmit, reason, rejectedBy, rejectedByLabel]);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle data-popup-header
        sx={{
          fontFamily: "Be Vietnam Pro",
          fontSize: 18,
          fontWeight: 700,
          color: TEXT,
          pb: 1.5,
        }}
      >
        {jobSpecific ? "Why are you passing on this candidate?" : candidateCount === 1 ? "Reject Candidate" : `Reject ${candidateCount} Candidates`}
      </DialogTitle>

      <Divider sx={{ borderColor: BORDER }} />

      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2.5}>
          {errors.submit && <Alert severity="error">{errors.submit}</Alert>}
          <Typography
            sx={{
              fontFamily: "Be Vietnam Pro",
              fontSize: 13,
              color: MUTED,
              lineHeight: 1.5,
            }}
          >
            {jobSpecific ? `This applies only to ${candidateCount === 1 ? "this candidate's match" : `these ${candidateCount} candidate matches`} for this job. Other job matches and the candidate profile remain unchanged.` : "This rejection will be logged in Activities with the selected reason and any additional note. Reason options are based on your user role."}
          </Typography>

          <Box>
            <Typography
              sx={{
                fontFamily: "Be Vietnam Pro",
                fontSize: 13,
                fontWeight: 700,
                color: TEXT,
                mb: 1,
              }}
            >
              Reason
            </Typography>
            <TextField
              select
              required
              label="Reason"
              disabled={busy}
              fullWidth
              size="small"
              value={reason}
              onChange={handleReasonChange}
              error={Boolean(errors.reason)}
              helperText={errors.reason || (reasonOptions.length ? "" : "No reasons available for this role.")}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  fontFamily: "Be Vietnam Pro",
                },
              }}
            >
              {reasonOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box>
            <Typography
              sx={{
                fontFamily: "Be Vietnam Pro",
                fontSize: 13,
                fontWeight: 700,
                color: TEXT,
                mb: 1,
              }}
            >
              {jobSpecific ? "Additional Feedback" : "Notes (Optional)"}
            </Typography>
            <TextField
              fullWidth
              multiline
              label={jobSpecific ? "Additional Feedback" : "Notes"}
              disabled={busy}
              inputProps={{ maxLength: 10000 }}
              minRows={4}
              value={notes}
              onChange={handleNotesChange}
              placeholder={jobSpecific ? "Candidate does not have enough enterprise SaaS experience." : "Additional Context"}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  fontFamily: "Be Vietnam Pro",
                },
              }}
            />
          </Box>
          {jobSpecific && <>
            <TextField select fullWidth label="Disposition" value={disposition} disabled={busy} onChange={event => setDisposition(event.target.value)}
              helperText="Records the next step for this candidate. Global profile actions are handled separately.">
              {JOB_DISPOSITIONS.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
            </TextField>
            <TextField select fullWidth label="Decision By" value={decisionBy} disabled={busy} onChange={event => setDecisionBy(event.target.value)}
              helperText="Who made the decision. The logged-in user is recorded separately.">
              {DECISION_SOURCES.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
            </TextField>
          </>}
        </Stack>
      </DialogContent>

      <DialogActions data-popup-footer sx={{ px: 3, pb: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{
            textTransform: "none",
            color: MUTED,
            fontWeight: 600,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={busy || !reason}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{
            textTransform: "none",
            borderRadius: 999,
            px: 2.5,
            background: "linear-gradient(135deg,#8B5CF6,#9333EA)",
            fontWeight: 700,
            "&:hover": {
              background: "linear-gradient(135deg,#7C3AED,#8B5CF6)",
            },
          }}
        >
          {busy ? "Saving..." : jobSpecific ? "Pass Candidate" : "Confirm Rejection"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
